import { useMemo } from 'react';

// Hook para derivar drivers únicos das locations
export const useDrivers = (locations) => {
  return useMemo(() => {
    if (!locations?.length) return [];

    return [...new Set(locations.map(location => location.driver))]
      .filter(Boolean)
      .sort();
  }, [locations]);
};

// Hook para derivar carriers únicos das locations
export const useCarriers = (locations) => {
  return useMemo(() => {
    if (!locations?.length) return [];

    const carrierMap = new Map();

    locations.forEach(location => {
      if (location.carrier_name && location.carrier_id) {
        carrierMap.set(location.carrier_id, [location.carrier_name, location.carrier_id]);
      }
    });

    return Array.from(carrierMap.values())
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [locations]);
};

// Hook para calcular estatísticas das locations
export const useLocationStats = (locations) => {
  return useMemo(() => {
    if (!locations?.length) {
      return [
        {
          title: 'Motoristas Ativos',
          value: 0,
          interval: 'Hoje',
          trend: 'neutral',
          data: new Array(30).fill(0)
        },
        {
          title: 'Velocidade Média',
          value: "0 km/h",
          interval: 'Hoje',
          trend: 'neutral',
          data: new Array(30).fill(0)
        },
        {
          title: 'Precisão GPS',
          value: "0m",
          interval: 'Média',
          trend: 'neutral',
          data: new Array(30).fill(0)
        },
      ];
    }

    // Calcular métricas
    const activeDrivers = new Set(locations.map(l => l.driver)).size;
    const speeds = locations
      .filter(l => l.speed && Number(l.speed) > 0)
      .map(l => Number(l.speed))
      .filter(speed => !isNaN(speed));
    const accuracies = locations
      .filter(l => l.accuracy && Number(l.accuracy) > 0)
      .map(l => Number(l.accuracy))
      .filter(accuracy => !isNaN(accuracy));
    const timestamps = locations.map(l => new Date(l.created_at));
    const lastUpdate = timestamps.sort((a, b) => b - a)[0];

    // Calcular médias
    const avgSpeed = speeds.length > 0 ? Math.round(speeds.reduce((a, b) => a + b, 0) / speeds.length) : 0;
    const avgAccuracy = accuracies.length > 0 ? Math.round(accuracies.reduce((a, b) => a + b, 0) / accuracies.length) : 0;

    // Gerar dados de tendência baseados nos valores reais
    const generateTrendData = (values, baseValue, count = 30) => {
      if (values.length === 0 || !baseValue || isNaN(baseValue)) {
        return new Array(count).fill(0);
      }

      const variance = Math.max(baseValue * 0.2, 5);
      return Array.from({ length: count }, () =>
        Math.max(0, Math.round(baseValue + (Math.random() - 0.5) * variance))
      );
    };

    return [
      {
        title: 'Motoristas Ativos',
        value: activeDrivers,
        interval: 'Hoje',
        trend: activeDrivers > 0 ? 'up' : 'neutral',
        data: generateTrendData([], activeDrivers)
      },
      {
        title: 'Velocidade Média',
        value: avgSpeed > 0 ? `${avgSpeed} km/h` : "0 km/h",
        interval: 'Hoje',
        trend: avgSpeed > 15 ? 'up' : avgSpeed > 0 ? 'neutral' : 'down',
        data: generateTrendData(speeds, avgSpeed)
      },
      {
        title: 'Precisão GPS',
        value: avgAccuracy > 0 ? `${avgAccuracy}m` : "0m",
        interval: 'Média',
        trend: avgAccuracy > 0 && avgAccuracy < 10 ? 'up' : avgAccuracy < 20 ? 'neutral' : 'down',
        data: generateTrendData(accuracies, avgAccuracy)
      },
    ];
  }, [locations]);
};

// Hook para filtrar locations
export const useFilteredLocations = (locations, selectedDriver, selectedCarrier) => {
  return useMemo(() => {
    if (!locations?.length) return [];

    let filtered = locations;

    if (selectedDriver) {
      filtered = filtered.filter(location => location.driver === selectedDriver);
    }

    if (selectedCarrier) {
      filtered = filtered.filter(location => location.carrier_id === selectedCarrier);
    }

    return filtered;
  }, [locations, selectedDriver, selectedCarrier]);
};

// Hook agregado que retorna todos os dados derivados
export const useLocationData = (locations, selectedDriver = '', selectedCarrier = '') => {
  const drivers = useDrivers(locations);
  const carriers = useCarriers(locations);
  const filteredLocations = useFilteredLocations(locations, selectedDriver, selectedCarrier);
  const stats = useLocationStats(filteredLocations);

  return {
    drivers,
    carriers,
    filteredLocations,
    stats,
    allLocations: locations
  };
}; 